import { isRequestGM } from "../components/requests/requestPermissions";
import { schedulingEntryBoutique } from "../lib/schedulingEntry";
import useFocusState from "../hooks/useFocusState";
import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import RequestVessel from "../components/requests/RequestVessel";
import SwapAgreementForm from "../components/requests/SwapAgreementForm";
import { swapStatus } from "../components/requests/requestForms";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Avatar from "../components/Avatar";
import {
  Plus, CheckCircle2, XCircle, Trash2, Repeat, ArrowRightLeft,
} from "lucide-react";

const STATUS_STYLES = {
  requested:      { dot: "bg-amber-500",   label: "Awaiting offers" },
  matched:        { dot: "bg-indigo-500",  label: "Offers waiting" },
  approved:       { dot: "bg-emerald-500", label: "Approved" },
  zenoti_pending: { dot: "bg-indigo-500",  label: "Zenoti pending" },
  completed:      { dot: "bg-emerald-500", label: "Completed" },
  cancelled:      { dot: "bg-stone-400",   label: "Cancelled" },
};

const AUDIENCE_LABELS = {
  boutique:    "Entire boutique",
  morning_egs: "Morning EGs",
  mts:         "Massage Therapists",
  egs:         "Experience Guides",
};

function fmtTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const hh = ((h + 11) % 12) + 1;
  const ampm = h >= 12 ? "PM" : "AM";
  return `${hh}:${String(m).padStart(2, "0")} ${ampm}`;
}

function StatusPill({ status, doc }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.requested;
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] schedule-muted bg-stone-50 px-2.5 py-0.5 rounded-full" data-testid={`swap-status-${status}`}>
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {doc ? swapStatus(doc) : s.label}
    </span>
  );
}

function ShiftLine({ shift, label }) {
  if (!shift) return null;
  return (
    <div className="text-sm schedule-ink">
      {label && <span className="text-[10px] label-eyebrow schedule-muted mr-2">{label}</span>}
      <span className="tabular-nums">{shift.date}</span>
      {shift.start && <span className="ml-2 schedule-muted">{fmtTime(shift.start)}–{fmtTime(shift.end)}</span>}
    </div>
  );
}

function SwapDetail({ opp, refresh, onUpgrade }) {
  const { user } = useAuth();
  const s = opp.swap || {};
  const offers = opp.volunteers || [];
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [declineReason, setDeclineReason] = useState("");
  const [showDecline, setShowDecline] = useState(false);

  const assign = async (volunteer_id) => {
    setBusy(true); setErr("");
    try { await api.post(`/scheduling/swaps/${opp.id}/assign`, { volunteer_id }); await refresh(); }
    catch (e) { setErr(e.response?.data?.detail || "Could not assign."); }
    finally { setBusy(false); }
  };
  const decline = async () => {
    if (!declineReason.trim()) { setErr("Reason required."); return; }
    setBusy(true); setErr("");
    try { await api.post(`/scheduling/swaps/${opp.id}/decline`, { reason: declineReason.trim() }); setShowDecline(false); setDeclineReason(""); await refresh(); }
    catch (e) { setErr(e.response?.data?.detail || "Could not decline."); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    if (!window.confirm("Cancel this swap post?")) return;
    setBusy(true);
    try { await api.post(`/scheduling/swaps/${opp.id}/cancel`, {}); await refresh(); }
    finally { setBusy(false); }
  };
  const withdraw = async () => {
    setBusy(true);
    try { await api.delete(`/scheduling/swaps/${opp.id}/offer`); await refresh(); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid={`swap-detail-${opp.id}`}>
      <div className="schedule-panel schedule-border rounded-3xl p-6 ">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div className="min-w-0">
            <div className="text-[10px] label-eyebrow schedule-muted">
              {s.mode === "strict_trade" ? "Strict trade" : "Give up / trade"} · {AUDIENCE_LABELS[opp.audience] || opp.audience}
            </div>
            <h2 className="font-serif-display schedule-ink text-2xl mt-1">
              {s.requester_employee_name || "Requester"}
            </h2>
            <div className="mt-3 space-y-1.5">
              <ShiftLine shift={s.my_shift} label="Giving up" />
              {s.target_shift && (
                <div className="flex items-center gap-2">
                  <ArrowRightLeft size={13} strokeWidth={1.5} className="schedule-muted" />
                  <ShiftLine shift={s.target_shift} label="Ideal counter" />
                </div>
              )}
            </div>
            {s.notes && <div className="text-sm schedule-muted mt-3 italic">"{s.notes}"</div>}
          </div>
          <StatusPill status={opp.status} doc={opp} />
        </div>
      </div>

      {opp.requires_agreement_upgrade && <div className="request-card" role="status"><h3>Both employee confirmations needed</h3><p>This historical request and its offers remain on record. Earlier offers are not signatures. Start a fresh named draft and have both employees confirm from their own accounts before submission to management.</p>{opp.is_my_swap && <button type="button" onClick={() => onUpgrade(opp)}>Start a named draft</button>}</div>}
      {/* Historical offers */}
      <div className="schedule-panel schedule-border rounded-3xl p-6 " data-testid="swap-offers">
        <div className="flex items-baseline justify-between">
          <h3 className="font-serif-display schedule-ink text-lg">Historical offers</h3>
          <span className="text-[11px] schedule-muted">{offers.length} {offers.length === 1 ? "offer" : "offers"}</span>
        </div>
        {offers.length === 0 ? (
          <div className="text-sm schedule-muted mt-3" data-testid="swap-no-offers">No offers yet.</div>
        ) : (
          <ul className="mt-3 divide-y divide-stone-100">
            {offers.map((v, index) => (
              <li key={`${v.employee_id}-${index}`} className="py-3 flex items-start gap-3" data-testid={`swap-offer-${v.employee_id}`}>
                <Avatar name={v.employee_name} size={28} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm schedule-ink">{v.employee_name}</div>
                  {v.offered_shift ? (
                    <div className="text-xs schedule-muted mt-0.5">
                      Offers in exchange: <span className="tabular-nums">{v.offered_shift.date}</span>
                      <span className="ml-2 schedule-muted">{fmtTime(v.offered_shift.start)}–{fmtTime(v.offered_shift.end)}</span>
                    </div>
                  ) : (
                    <div className="text-xs schedule-muted mt-0.5">Bare takeover (no counter-shift)</div>
                  )}
                  {v.note && <div className="text-[11px] schedule-muted italic mt-0.5">"{v.note}"</div>}
                  <div className="text-[10px] schedule-muted mt-0.5">
                    offered {new Date(v.volunteered_at).toLocaleString()}{v.withdrawn_at ? ` · ${v.declined_by_manager ? "declined by manager" : "withdrawn"} ${new Date(v.withdrawn_at).toLocaleString()}` : ""}
                  </div>
                </div>
                {opp.status === "approved" && opp.assigned_employee_id === v.employee_id && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Approved</span>
                )}
                {isRequestGM(user, opp.boutique_code) && opp.can_assign && (
                  <button onClick={() => assign(v.employee_id)} disabled={busy}
                    data-testid={`swap-assign-${v.employee_id}`}
                    className="text-[11px] px-3 py-1 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-40">
                    Approve
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {opp.status === "approved" && (
        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4 text-sm text-emerald-900" data-testid="swap-approved-banner">
          <CheckCircle2 size={14} strokeWidth={1.5} className="inline mr-2" />
          Approved — {opp.assigned_employee_name} takes the shift. A Zenoti update task has been queued.
        </div>
      )}

      {opp.approvals?.decision && <div className="request-card"><p>Historical management decision: {opp.approvals.decision} · {opp.approvals.manager_name || "Recorded manager"}{opp.approvals.decided_at ? ` · ${new Date(opp.approvals.decided_at).toLocaleString()}` : ""}</p></div>}
      {opp.audit?.length > 0 && <details className="request-card"><summary>Original request history</summary><ol className="request-history">{opp.audit.map((event,index)=><li key={index}>{(event.action || "recorded").replace(/_/g," ")}{event.actor_name ? ` · ${event.actor_name}` : ""}{event.at ? ` · ${new Date(event.at).toLocaleString()}` : ""}</li>)}</ol></details>}
      {/* Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        {opp.already_volunteered && ["requested", "matched"].includes(opp.status) && (
          <button onClick={withdraw} disabled={busy} data-testid="swap-withdraw-btn"
            className="text-sm px-4 py-2 rounded-full schedule-border schedule-ink hover:bg-stone-50">
            Withdraw offer
          </button>
        )}
        {isRequestGM(user, opp.boutique_code) && opp.can_decline && (
          <button onClick={() => setShowDecline((v) => !v)} data-testid="swap-decline-btn"
            className="text-sm px-4 py-2 rounded-full schedule-border schedule-ink hover:bg-stone-50 flex items-center gap-2">
            <XCircle size={14} strokeWidth={1.5} /> Decline offers
          </button>
        )}
        {opp.can_cancel && (
          <button onClick={cancel} disabled={busy} data-testid="swap-cancel-btn"
            className="text-sm schedule-muted hover:text-rose-600 flex items-center gap-1.5 ml-auto">
            <Trash2 size={12} strokeWidth={1.5} /> Cancel post
          </button>
        )}
      </div>

      {showDecline && (
        <div className="bg-stone-50 rounded-2xl p-4 flex flex-wrap items-center gap-2" data-testid="swap-decline-form">
          <input type="text" placeholder="Reason for declining these offers…"
            value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
            data-testid="swap-decline-reason" aria-label="Reason for declining"
            className="flex-1 min-w-0 px-3 py-1.5 rounded-lg schedule-border text-sm outline-none schedule-panel" />
          <button onClick={decline} disabled={busy}
            data-testid="swap-decline-confirm"
            className="text-sm px-4 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-40">
            {busy ? "…" : "Decline"}
          </button>
        </div>
      )}

      {err && <div className="text-sm text-rose-600" data-testid="swap-detail-error">{err}</div>}
    </div>
  );
}

function NamedSwapDetail({ opp, user, refresh }) {
  const [busy,setBusy]=useState(false), [error,setError]=useState(""), [reason,setReason]=useState("");
  const act=async(path,payload)=>{setBusy(true);setError("");try{await api.post(path,payload);await refresh();}catch(e){setError(e.response?.data?.detail || "The decision could not be confirmed. Refresh and retry.");}finally{setBusy(false);}};
  return <div><SwapAgreementForm user={user} boutique={opp.boutique_code} initial={opp} onChanged={refresh}/>
    {isRequestGM(user, opp.boutique_code) && opp.can_assign && <div className="request-card" style={{marginTop:18}}><h2>Manager review</h2><p>Both employees confirmed revision {opp.agreement.revision}. Approval queues the existing Zenoti update task; it does not change the schedule automatically.</p><label className="request-field">Decision note<input value={reason} maxLength={1000} onChange={e=>setReason(e.target.value)}/></label><div className="request-actions"><button type="button" className="request-primary" disabled={busy} onClick={()=>act(`/scheduling/swaps/${opp.id}/assign`,{volunteer_id:opp.agreement.terms.coworker_id,notes:reason})}>Approve named swap</button>{isRequestGM(user, opp.boutique_code) && opp.can_decline && <button type="button" disabled={busy || !reason.trim()} onClick={()=>act(`/scheduling/swaps/${opp.id}/decline`,{reason})}>Decline named swap</button>}</div></div>}
    {opp.can_cancel && <div className="request-actions"><button disabled={busy} onClick={()=>act(`/scheduling/swaps/${opp.id}/cancel`,{reason:"Cancelled by requester or authorized manager"})}>Cancel draft</button></div>}
    {error && <p role="alert" className="request-message">{error}</p>}
  </div>;
}

function SwapListItem({ opp, active, onOpen }) {
  const s = opp.swap || {};
  const my = s.my_shift || {};
  const vols = (opp.volunteers || []).filter((v) => !v.withdrawn_at).length;
  return (
    <button onClick={() => onOpen(opp.id)} data-testid={`swap-item-${opp.id}`} aria-pressed={active}
      className={`schedule-list-row w-full text-left p-4 flex items-center gap-3 transition-colors ${active ? "bg-stone-50" : "hover:bg-stone-50"}`}>
      <div className="w-10 h-10 rounded-2xl bg-stone-50 schedule-border flex items-center justify-center schedule-ink">
        <Repeat size={16} strokeWidth={1.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm schedule-ink truncate">
          {s.requester_employee_name} · {my.date}
        </div>
        <div className="text-[11px] schedule-muted truncate">
          {fmtTime(my.start)}–{fmtTime(my.end)}
          {s.mode === "strict_trade" && " · strict"}
          {vols > 0 && ` · ${vols} offer${vols !== 1 ? "s" : ""}`}
        </div>
      </div>
      <StatusPill status={opp.status} doc={opp} />
    </button>
  );
}

export default function Swaps({ mode = "manager" }) {
  const { user } = useAuth();
  const [entryParams] = useSearchParams();
  const entryBoutique = schedulingEntryBoutique(user, entryParams.get("boutique"));
  const { oppId } = useParams();
  const navigate = useNavigate();
  const [boutique, setBoutique] = useState(entryBoutique);
  const [tab, setTab] = useFocusState(mode === "employee" ? "available" : entryParams.get("view") === "all" ? "all" : "matched");
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(oppId || null);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [legacy, setLegacy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [detailError, setDetailError] = useState("");
  const isLeadership = user && !user.boutique_code;
  const isFrontline = user && ["front_desk", "experience_guide", "lead_experience_guide", "massage_therapist"].includes(user.role);
  const boutiques = ["PLT", "FLL", "WYN", "PNC"];

  useEffect(() => setSelectedId(oppId || null), [oppId]);

  const loadList = useCallback(async () => {
    setLoading(true); setListError("");
    try {
      const params = {};
      if (mode === "employee") {
        if (tab === "available") params.audience_for_me = true;
        else if (tab === "mine") params.mine = true;
      } else {
        params.boutique = boutique;
        if (tab === "matched")   params.status = "matched";
        else if (tab === "requested") params.status = "requested";
      }
      const r = await api.get("/scheduling/swaps", { params });
      const list = r.data?.swaps || [];
      setItems(list);
    } catch { setListError("Requests could not load. Retry to retrieve the saved records."); } finally { setLoading(false); }
  }, [boutique, mode, tab]);
  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    if (!selectedId) { setSelected(null); setDetailError(""); return; }
    setDetailError("");
    api.get(`/scheduling/swaps/${selectedId}`).then((r) => setSelected(r.data)).catch(() => { setSelected(null); setDetailError("Request details could not load. Retry to retrieve the saved record."); });
  }, [selectedId]);

  const refresh = async () => {
    await loadList();
    if (selectedId) {
      try {
        const r = await api.get(`/scheduling/swaps/${selectedId}`);
        setSelected(r.data); setDetailError("");
      } catch { setDetailError("Request details could not refresh. Retry to retrieve the saved record."); }
    }
  };

  const description = mode === "employee"
    ? "Two shifts. A shared plan. Every handoff clear."
    : "Review named swaps after both employees confirm.";
  const managerTabs = [
    { id: "matched", label: "Pending review" },
    { id: "requested", label: "Needs fresh confirmations" },
    { id: "all", label: "All history" },
  ];
  const employeeTabs = [
    { id: "available", label: "Needs my agreement" },
    { id: "mine", label: "My swaps" },
  ];
  const tabs = mode === "employee" ? employeeTabs : managerTabs;

  return (
    <RequestVessel swap title="Shift Swap" description={description} backTo={mode === "employee" ? "/my-schedule" : "/scheduling"} backLabel={mode === "employee" ? "My schedule" : "Scheduling"} toolbar={<div className="flex items-center justify-end gap-2 flex-wrap">

            {isLeadership && mode === "manager" && (
              <select value={boutique} onChange={(e) => setBoutique(e.target.value)} data-testid="swaps-boutique" aria-label="Boutique"
                className="px-4 py-2 rounded-full schedule-border text-sm schedule-panel outline-none">
                {boutiques.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            )}
            {isFrontline && (
              <button onClick={() => { setLegacy(null); setShowForm((v) => !v); }} data-testid="swaps-new-toggle" aria-expanded={showForm}
                className="text-sm px-4 py-2 rounded-full bg-stone-900 text-white hover:bg-stone-800 flex items-center gap-2">
                <Plus size={14} strokeWidth={1.5} /> New swap
              </button>
            )}
            {showForm && <button type="button" onClick={() => setShowForm(false)}>Request history</button>}
        </div>}>


        {showForm && (
          <div className="mt-6 request-form-wrap">
            <SwapAgreementForm key={legacy?.id || "new"} user={user} boutique={boutique} legacy={legacy} onChanged={() => { refresh(); }} />
          </div>
        )}

        {listError && <div role="alert" className="request-message">{listError}<button type="button" onClick={loadList}>Retry request list</button></div>}
        {detailError && <div role="alert" className="request-message">{detailError}<button type="button" onClick={refresh}>Retry request details</button></div>}
        {!showForm && <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-1 space-y-3">
            <div className="sonmi-nav sonmi-nav--primary flex items-center gap-1" data-testid="swaps-tabs">
              {tabs.map((t) => (
                <button key={t.id} onClick={() => setTab(t.id)} data-testid={`swaps-tab-${t.id}`} aria-pressed={tab === t.id}
                  className={`text-xs px-3 py-1.5 rounded-full ${tab === t.id ? "bg-stone-900 text-white" : "schedule-panel schedule-border schedule-muted hover:bg-stone-50"}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="schedule-panel schedule-border rounded-2xl divide-y divide-stone-100 overflow-hidden" data-testid="swaps-list">
              {loading ? (
                <div className="p-6 text-sm schedule-muted">Loading…</div>
              ) : items.length === 0 ? (
                <div className="p-6 text-sm schedule-muted" data-testid="swaps-list-empty">
                  {mode === "employee" ? "No named swaps awaiting your agreement." : "Nothing in this bucket yet."}
                </div>
              ) : items.map((o) => (
                <SwapListItem key={o.id} opp={o} active={selectedId === o.id}
                  onOpen={(id) => { setSelectedId(id); navigate(mode === "employee" ? `/help-a-teammate/swaps/${id}` : `/scheduling/swaps/${id}`); }} />
              ))}
            </div>
          </div>
          <div className="md:col-span-2">
            {selected ? (
              selected.agreement ? <NamedSwapDetail key={selected.id} opp={selected} user={user} refresh={refresh} /> : <SwapDetail opp={selected} refresh={refresh} onUpgrade={opp => { setLegacy(opp); setShowForm(true); }} />
            ) : (
              <div className="schedule-panel schedule-border rounded-3xl p-10 text-sm schedule-muted text-center">
                {mode === "employee"
                  ? "Pick a swap on the left to see the shifts involved."
                  : "Pick a submitted swap on the left to review."}
              </div>
            )}
          </div>
        </div>}
    </RequestVessel>
  );
}
